import type { GestureResponderHandlers, GestureResponderEvent } from "react-native";

import { isWeb } from "../../util/isWeb";

import { log as logMobUserInteractionDetected } from "@indeed/signals-schema/com/indeed/signals/schema/jobsearch/mobUserInteractionDetected";
import { useCallback, useEffect, useMemo, useRef } from "react";

/**
 * Hook to compose pan handlers with first user interaction logging
 * This tracks the first touch/interaction event and logs it via signals
 *
 * On web an additional `onMouseOver` handler is composed in, so desktop users
 * who move the cursor without ever pressing are still counted (RN-7331).
 *
 * @param mobtk - Mobile tracking key for the page
 * @param from - Source/referrer parameter (e.g., where the user came from)
 * @param panHandlers - Original gesture responder handlers to compose with
 * @returns Composed pan handlers with interaction logging
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const mobtk = useTrackingKey();
 *   const from = useFromParam();
 *   const originalPanResponder = usePerformanceMeasurement({ ... });
 *
 *   const composedHandlers = useLogFirstUserInteractionWithHandlers(
 *     mobtk,
 *     from,
 *     originalPanResponder.panHandlers
 *   );
 *
 *   return <View {...composedHandlers} />;
 * }
 * ```
 */
export function useLogFirstUserInteractionWithHandlers(
  mobtk: string | null | undefined,
  from: string | null | undefined,
  panHandlers: GestureResponderHandlers,
) {
  const hasLoggedRef = useRef(false);

  // Reset when mobtk changes — a new mobtk indicates a new page load (e.g. pull-to-refresh)
  useEffect(() => {
    hasLoggedRef.current = false;
  }, [mobtk]);

  const logFirstInteraction = useCallback(
    (interactionType: string = "touch") => {
      // Only log once per page load
      if (hasLoggedRef.current) {
        return;
      }

      hasLoggedRef.current = true;

      logMobUserInteractionDetected({
        mobtk: mobtk ? { string: mobtk } : null,
        from: from ? { string: from } : null,
        interactionType: { string: interactionType },
        v: { string: "0" },
      });
    },
    [mobtk, from],
  );

  const composedHandlers = useMemo(() => {
    return {
      ...panHandlers,
      ...(isWeb ? { onMouseOver: () => logFirstInteraction("mousemove") } : {}),
      // Capture phase (top-down): log first interaction for every touch/scroll before any
      // child claims the responder. Returning false ensures we never steal the gesture.
      // This improves reliability when the first interaction is scroll or on a button/link.
      onStartShouldSetResponderCapture: () => {
        logFirstInteraction("touch");
        return false;
      },
      onMoveShouldSetResponderCapture: () => {
        logFirstInteraction("scroll");
        return false;
      },
      onStartShouldSetResponder: (e: GestureResponderEvent) => {
        logFirstInteraction("touch");
        return panHandlers.onStartShouldSetResponder?.(e) ?? false;
      },
      onMoveShouldSetResponder: (e: GestureResponderEvent) => {
        logFirstInteraction("scroll");
        return panHandlers.onMoveShouldSetResponder?.(e) ?? false;
      },
    };
  }, [panHandlers, logFirstInteraction]);

  return composedHandlers;
}
